import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * Duration entry control. Operator types a number and picks a unit
 * (minutes / hours / days / months). Storage is always minutes — we
 * convert on submit and re-decompose on read.
 *
 * Read-time unit pick: largest unit that gives a whole number, with
 * ONE exception — half hours. 90 min reads back as "1.5 hours", not
 * "90 minutes", so an operator who typed 1.5 sees 1.5 again when they
 * reopen the rate. 1440 min becomes "1 day". 43200 becomes "1 month".
 *
 * HALF HOURS (Ray, 2026-08-30). Operators need a 1h30m rate. Two
 * defects had to be fixed for that to be typeable at all:
 *
 *   - The box was <input type="number">. Typing "1" then "." leaves the
 *     field holding "1.", which is not a valid number, so the browser
 *     returns "" — we blanked the box and the next keystroke landed on
 *     the browser's own leftover buffer. Typing 1.5 produced 15, and
 *     with the unit on hours that silently SAVED 15 HOURS. It is now a
 *     text input with inputMode="decimal" and its own string buffer.
 *
 *   - decomposeMinutes only picked a unit that divided evenly, so a
 *     saved 90 came back as "90 minutes" and the entry changed shape.
 *
 * Fractions are allowed on HOURS ONLY, and only in halves. 1.5 = 1h30m.
 * Everything else stays whole: 1.5 minutes is meaningless, and 1.5 days
 * is 36 hours, which an operator would enter as hours anyway.
 *
 * 🔴 The 1.30 trap. A decimal field reads 1.30 as 1.3 hours = 78
 * minutes, NOT 1h30m. That is the number an operator is most likely to
 * type, so anything but .0 or .5 is REFUSED inline and never reaches
 * the parent — see HALF_STEP below.
 *
 * The refusal names the number to type INSTEAD ("switch to minutes and
 * type 96"), because nothing is actually locked out — the minutes unit
 * still takes any whole number. Only the hours unit is constrained, and
 * only because that is where 1.30 could quietly mean 78 minutes.
 *
 * `value` semantics:
 *   number      → minutes
 *   null        → "use default" (only when allowDefault is true)
 *   undefined   → blank (only useful for new-row inserts; treated as
 *                  null externally)
 *
 * When allowDefault is true, the unit dropdown carries an extra
 * "Default (24 h)" entry. Picking it sets `value` to null. Used for
 * the per-rate pause-window override.
 */

type Unit = 'minutes' | 'hours' | 'days' | 'months';

const UNIT_MINUTES: Record<Unit, number> = {
   minutes: 1,
   hours: 60,
   days: 60 * 24,
   months: 60 * 24 * 30, // "month" = flat 30 days
};

const UNIT_LABEL: Record<Unit, string> = {
   minutes: 'minutes',
   hours: 'hours',
   days: 'days',
   months: 'months',
};

/**
 * Pick the largest unit that gives a whole value, defaulting to minutes
 * when nothing divides cleanly — except half hours, which come back as
 * a fractional HOURS value (90 → 1.5) so a 1h30m rate reopens the way
 * it was typed.
 */
export function decomposeMinutes(mins: number): { value: number; unit: Unit } {
   if (!Number.isFinite(mins) || mins < 0) return { value: 0, unit: 'minutes' };
   // Exact divisors first, largest unit wins. 'minutes' is deliberately
   // NOT in this list — it is the fallback below, so the half-hour case
   // gets a look in before everything collapses to minutes.
   const order: Unit[] = ['months', 'days', 'hours'];
   for (const u of order) {
      const divisor = UNIT_MINUTES[u];
      if (mins >= divisor && mins % divisor === 0) {
         return { value: mins / divisor, unit: u };
      }
   }
   // Half hours: 90 → 1.5 hours. Only at or above an hour — 30 minutes
   // reads better as "30 minutes" than as "0.5 hours".
   if (mins >= 60 && mins % 30 === 0) {
      return { value: mins / 60, unit: 'hours' };
   }
   return { value: mins, unit: 'minutes' };
}

/** Hours may be entered in halves (1.5 = 1h30m). Every other unit is
 *  whole-number only. */
const HALF_STEP = 0.5;

/** True when `n` is a legal entry for `unit`. Guards the 1.30 trap:
 *  1.3 hours is 78 minutes, which is never what the operator meant. */
export function isValidForUnit(n: number, unit: Unit): boolean {
   if (!Number.isFinite(n) || n < 0) return false;
   if (unit !== 'hours') return Number.isInteger(n);
   // Compare in half-steps to dodge float dust (1.5/0.5 = 2.9999…).
   return Number.isInteger(Math.round(n / HALF_STEP * 1e6) / 1e6);
}

export function composeMinutes(value: number, unit: Unit): number {
   return Math.round(value * UNIT_MINUTES[unit]);
}

interface Props {
   value: number | null | undefined;
   onChange: (next: number | null) => void;
   /** When true, dropdown shows a "Default (deviceDefaultMinutes)" option
    *  that maps to null. Used for the per-rate pause-window override. */
   allowDefault?: boolean;
   /** Required when allowDefault is true — used to label the Default option. */
   deviceDefaultMinutes?: number;
   /** Lower bound on the number input. Defaults to 1 (rates must be > 0). */
   min?: number;
   disabled?: boolean;
   /** Tightens the value input column when packing into a dense row. */
   compact?: boolean;
   /** Report the refusal UPWARD instead of printing it under the box.
    *  Inside a rates ROW the inline message grew the row and pushed
    *  Price / Pause limit / Label out of line with their neighbours, so
    *  the rates editor collects the message and shows one full-width
    *  banner. The red border stays either way — the operator still has
    *  to see WHICH box is wrong. */
   onError?: (message: string | null) => void;
   /** Hint shown while the box is empty. Defaults to the minimum ("1"),
    *  which reads like a real value in a box with no rate next to it — the
    *  free time promo says "Type" instead (Ray 2026-10-30). */
   placeholder?: string;
}

function DurationUnitInput({
   value,
   onChange,
   allowDefault = false,
   deviceDefaultMinutes,
   min = 1,
   disabled,
   compact,
   onError,
   placeholder,
}: Props) {
   // Internal display state. Recomputes from `value` when it changes
   // externally, but holds local edits between renders so typing doesn't
   // jump on the parent's re-render.
   const initial = useMemo(() => {
      if (value === null || value === undefined) return { value: '', unit: 'minutes' as Unit };
      const d = decomposeMinutes(value);
      return { value: String(d.value), unit: d.unit };
   }, [value]);

   const [localValue, setLocalValue] = useState(initial.value);
   const [unit, setUnit] = useState<Unit>(initial.unit);
   // Inline message for a refused entry (the 1.30 trap). Never a native
   // browser dialog — those are banned in this codebase.
   const [error, setError] = useState<string | null>(null);
   // The "1.5 = 1h 30m" tip is only shown while the operator is IN the
   // box. Under every saved row it is just repeated clutter.
   const [focused, setFocused] = useState(false);
   const onErrorRef = useRef(onError);
   onErrorRef.current = onError;
   /** Single place that sets the message, so the owner is always told. */
   const report = (msg: string | null) => {
      setError(msg);
      onErrorRef.current?.(msg);
   };

   // Re-sync when the external value changes (e.g., another field's
   // save round-trip brought back the canonical minutes form).
   //
   // Skip the echo of our OWN emit. Without this guard the parent's
   // re-render re-decomposes what we just sent and rewrites the box
   // mid-entry: typing "60" with the unit on minutes bounced back as
   // "1 hours", and a half-typed "1." was wiped before the "5" landed.
   useEffect(() => {
      const showing =
         localValue === '' || !isValidForUnit(Number(localValue), unit)
            ? null
            : composeMinutes(Number(localValue), unit);
      if (showing === value) return;

      const d = value === null || value === undefined
         ? { value: '', unit: 'minutes' as Unit }
         : (() => {
              const x = decomposeMinutes(value);
              return { value: String(x.value), unit: x.unit };
           })();
      setLocalValue(d.value);
      setUnit(d.unit);
      report(null);
      // localValue/unit are read only to detect the echo — re-running on
      // every keystroke would defeat the guard.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [value]);

   useEffect(() => () => onErrorRef.current?.(null), []);

   const isDefault = allowDefault && (value === null || value === undefined);
   const defaultLabel =
      allowDefault && deviceDefaultMinutes != null
         ? `Default (${defaultDisplay(deviceDefaultMinutes)})`
         : 'Default';

   const handleValueChange = (raw: string) => {
      // Digits with at most one dot. Anything else is not a keystroke we
      // can make sense of, so drop it rather than let it into the buffer.
      if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
      setLocalValue(raw);
      report(null);

      if (raw === '') {
         // Empty input — treat as "set null" only when default is allowed
         // (otherwise wait for a valid number).
         if (allowDefault) onChange(null);
         return;
      }
      // "1." and "." are mid-typing states, not numbers. Hold them in the
      // box and wait for the next keystroke — this is the bug that used
      // to turn 1.5 into 15.
      if (raw === '.' || raw.endsWith('.')) return;

      const n = Number(raw);
      if (!Number.isFinite(n)) return;
      // Bound in MINUTES, not in whatever unit is selected — otherwise
      // min=1 would reject 0.5 hours, which is a perfectly good 30-minute
      // rate. Blur reports anything still out of bounds.
      if (!isValidForUnit(n, unit)) return;
      const mins = composeMinutes(n, unit);
      if (mins < min) return;
      onChange(mins);
   };

   // Validation lands on blur, not per keystroke: mid-entry "1.3" is on
   // its way to a legal "1.35"-shaped mistake, and shouting at every
   // character makes the field unusable.
   const handleBlur = () => {
      if (localValue === '' || localValue.endsWith('.')) {
         report(null);
         return;
      }
      const n = Number(localValue);
      if (!Number.isFinite(n)) return;
      if (!isValidForUnit(n, unit)) {
         // Name the number they should type instead. "1.6 is not allowed"
         // leaves the operator to do the arithmetic; "switch to minutes
         // and type 96" is the whole answer.
         // Short words, and the exact number to type. The operators
         // reading this are not native English speakers — an explained
         // RULE costs them more than two worked examples do.
         report(
            unit === 'hours'
               ? `Use 1, 1.5, 2 or 2.5. For ${n} hours, choose "minutes" and type ${Math.round(
                    n * 60
                 )}.`
               : 'No decimals here. Use a whole number.'
         );
         return;
      }
      if (composeMinutes(n, unit) < min) {
         report(`Must be at least ${min} minute${min === 1 ? '' : 's'}.`);
         return;
      }
      report(null);
   };

   const handleUnitChange = (next: Unit | 'default') => {
      report(null);
      if (next === 'default') {
         setLocalValue('');
         setUnit('minutes');
         onChange(null);
         return;
      }
      setUnit(next);
      if (localValue !== '') {
         const n = Number(localValue);
         // Switching hours→days on a 1.5 leaves an illegal 1.5 days, so
         // re-check against the NEW unit before emitting.
         if (Number.isFinite(n) && isValidForUnit(n, next)) {
            const mins = composeMinutes(n, next);
            if (mins >= min) onChange(mins);
         }
      }
   };

   return (
      <Wrap>
      <Row $compact={compact}>
         {/* type="text", NOT type="number". A number input returns "" for
             the intermediate "1." and the decimal point never survives —
             that is what turned a typed 1.5 into 15. inputMode keeps the
             numeric keypad on mobile. */}
         <NumInput
            type="text"
            inputMode="decimal"
            value={isDefault ? '' : localValue}
            placeholder={isDefault ? '⌀' : placeholder ?? String(min)}
            disabled={disabled}
            $invalid={!!error}
            onChange={(e) => handleValueChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
               setFocused(false);
               handleBlur();
            }}
            $compact={compact}
         />
         <UnitSelect
            value={isDefault ? 'default' : unit}
            disabled={disabled}
            onChange={(e) => handleUnitChange(e.target.value as Unit | 'default')}
            title={unit === 'months' ? '1 month = 30 days' : undefined}
         >
            {allowDefault && <option value="default">{defaultLabel}</option>}
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
            <option value="months" title="1 month = 30 days">month</option>
         </UnitSelect>
      </Row>
      {focused && unit === 'hours' && !error && !isDefault && (
         <Hint>1.5 = 1h 30m</Hint>
      )}
      {error && !onError && <ErrorText role="alert">{error}</ErrorText>}
      </Wrap>
   );
}

function defaultDisplay(mins: number): string {
   const d = decomposeMinutes(mins);
   return `${d.value} ${UNIT_LABEL[d.unit]}`;
}

const Row = styled.div<{ $compact?: boolean }>`
   display: flex;
   gap: 4px;
   align-items: center;
   width: ${({ $compact }) => ($compact ? '100%' : 'auto')};
`;

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 3px;
   min-width: 0;
   position: relative;
`;

/* Absolutely positioned so appearing on focus cannot change the row's
   height — that jiggle knocked the neighbouring fields out of line. */
const Hint = styled.span`
   position: absolute;
   top: 100%;
   left: 2px;
   margin-top: 2px;
   white-space: nowrap;
   pointer-events: none;
   font-size: 10px;
   color: ${colors.GRAY8};
   line-height: 1.2;
`;

const ErrorText = styled.span`
   font-size: 10px;
   color: ${colors.DANGER};
   line-height: 1.3;
`;

const NumInput = styled.input<{ $compact?: boolean; $invalid?: boolean }>`
   width: ${({ $compact }) => ($compact ? '56px' : '70px')};
   /* flex-basis wins over the card's "width: 100% !important" on phones, so
      the number takes what is left and the unit stays readable. */
   flex: ${({ $compact }) => ($compact ? '1 1 56px' : '0 0 auto')};
   padding: 6px 8px;
   font-size: 12px;
   border: 1px solid ${({ $invalid }) => ($invalid ? colors.DANGER : colors.GRAY12)};
   border-radius: 6px;
   font-family: inherit;
   text-align: right;
   &:focus {
      outline: none;
      border-color: ${({ $invalid }) => ($invalid ? colors.DANGER : colors.PRIMARY)};
   }
   &:disabled {
      background: ${colors.GRAY14};
      color: ${colors.GRAY3};
   }
`;

const UnitSelect = styled.select`
   padding: 6px 6px;
   font-size: 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   background: white;
   font-family: inherit;
   /* A fixed, readable width for the unit. On the phone rate card every
      input and select is forced to width:100%, and with flex:1 / min-width:0
      this select shrank to a bare chevron — the operator could not see
      "minutes" or "days" (Ray, 2026-09-14). flex-basis beats width there. */
   flex: 0 0 104px;
   min-width: 104px;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
   &:disabled {
      background: ${colors.GRAY14};
      color: ${colors.GRAY3};
   }
`;

export default DurationUnitInput;
